/**
 * Single source of truth for every route of patrol-web's MARKETING-WEBSITE
 * portal (`www.patrolpuffin.com`).
 *
 * WHY THIS IS NOT `PATROL_ROUTES` (PORTAL-ROUTE-REGISTRIES-001, ruling Q-1)
 * ------------------------------------------------------------------------
 * `src/portals/marketing-website/**` is a SEPARATE React-Router app from the
 * tenant SPA: `src/main.jsx` hands three roots to `<SubdomainRouter brand="patrol">`
 * — `marketingApp`, `companyApp`, `platformAdminApp` — and the HOSTNAME picks
 * exactly one, so this router and `App.jsx`'s router never coexist in a page.
 *
 * Both declare `/login`, for different people: this one is the cross-domain
 * marketing login (MKT-LOGIN-XDOMAIN-001) that asks a visitor which tenant they
 * belong to; `PATROL_ROUTES.LOGIN` is the tenant sign-in on
 * `{tenant}.patrolpuffin.com`. Folding the two registries together would make
 * that collision look like a duplicate to resolve, and would let a constant
 * "resolve" against a route that only exists on the other host — the exact hole
 * `appRouteRegistries.contract.test.js` strips `src/portals/` to avoid.
 *
 * WHERE THE ROUTER DECLARES THESE
 * -------------------------------
 * `MarketingWebsiteApp.jsx` passes a `routes: [{ path, element }]` ARRAY to
 * `@puffin-core/marketing-shell`'s `createMarketingApp` — a config table, not
 * `<Route>` JSX. That form was INVISIBLE to the ecosystem's route resolver until
 * PORTAL-ROUTE-REGISTRIES-001 Phase 1 taught it to read route tables and to
 * follow the `...BLOG_TEMPLATE_ROUTES` spread into the package. Before that, a
 * registry here would have been judged against an EMPTY route set — every
 * constant "resolving" because nothing contradicted it.
 *
 * The route DECLARATIONS stay literal in that array on purpose: making them
 * symbolic would blind the resolver to the very routes it exists to read.
 *
 * Adding a route:
 *   1. Add `{ path: '/x', element: <XPage /> }` to `MarketingWebsiteApp.jsx`.
 *   2. Add the same `KEY: '/x'` here.
 *   3. Reference it via this registry from every link.
 *
 * @module portals/marketing-website/constants/patrolMarketingRoutePaths
 */

/**
 * Canonical marketing-website route paths.
 *
 * `HOME` is `'/'` and that is deliberate: unlike the tenant app — where `/` is a
 * redirect source and therefore NOT a registry destination — this router mounts
 * the landing page AT `/`. It is a real page, the 404's first quick link, the
 * header lockup's target, and the Features CTA's secondary destination.
 *
 * Only the parameterless `/blog` index is named. The four `:slug` / `:category`
 * / `:tag` / `:page` patterns come from `@puffin-core/marketing-shell`'s
 * `BLOG_TEMPLATE_ROUTES` (BLOG-STD-001) and are linked from inside that package,
 * never by hand from this app — so naming them here would create constants no
 * file references, which the registry's own orphan check would fail.
 */
export const PATROL_MARKETING_ROUTES = Object.freeze({
  HOME: '/',
  FEATURES: '/features',
  PRICING: '/pricing',
  CONTACT: '/contact',
  BLOG: '/blog',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  LOGIN: '/login',
});
